/**
 * <CtaLink> — un CTA interno de la app, medido.
 *
 * NO cambia el aspecto ni la navegación. Renderiza EXACTAMENTE el elemento que
 * le pases en `as`, le reenvía todas las props tal cual, y solo hace dos cosas
 * más: cuelga un observer para contar la impresión y mete un `onClick` que
 * dispara el evento antes de llamar al tuyo.
 *
 * Sin envoltorios, y es importante: los CTAs de la landing son hijos
 * `inline-flex` con clases `h-12 px-6` exactas dentro de una fila con `gap`, y
 * un `<span>` de más rompería la maquetación en todas ellas.
 *
 * Migrar un CTA es cambiar `<a` / `<Link` / `<Button` por `<CtaLink as={...}` y
 * añadir `cta="..."`. Cero cambios de clases.
 *
 *   <CtaLink as="a" cta="landing.hero.precios" href="#precios" className="…">
 *   <CtaLink as={Link} cta="landing.nav.registro" to="/register" className="…">
 *   <CtaLink as={Button} cta="modal.upsell.abrir" onClick={() => setOpen(true)}>
 *
 * Cuando el botón se construye desde un mapa de acciones y cambiar el tipo de
 * elemento sería invasivo, usa el hook suelto en su lugar:
 *
 *   const track = useTrackCta();
 *   onClick={() => { track('modal.business-todos.upgrade'); accion.onClick(); }}
 *
 * Un `cta` que no esté en `lib/ctaRegistry.js` no manda nada y avisa por
 * consola en desarrollo; el test del registro lo hace fallar en CI.
 *
 * Dentro de `<CtaSinMedir>` no se mide nada (ni impresión ni click): es para
 * las vistas previas que abre un admin (Admin → Campaña pt). Las impresiones
 * van a un contador diario que no sabe quién las hizo, así que un admin
 * revisando la landing inflaría la tasa de altas de cada botón.
 */
import { createContext, useCallback, useContext } from 'react';

import { trackCtaClick, useCtaImpression } from '@/lib/ctaTracking';

/**
 * @param {object} props
 * @param {string} props.cta Id declarado en `lib/ctaRegistry.js`.
 * @param {React.ElementType} [props.as] Elemento a renderizar. Por defecto `'a'`.
 *   Vale cualquiera que reenvíe `onClick`, `className` y `ref`: `'a'`,
 *   `'button'`, el `Link` de react-router y el `Button` de shadcn lo hacen.
 */
const SinMedirContext = createContext(false);

/** Todo lo que cuelgue de aquí se pinta igual pero no manda eventos de CTA. */
export function CtaSinMedir({ children }) {
  return <SinMedirContext.Provider value>{children}</SinMedirContext.Provider>;
}

/** ¿Estamos dentro de un `<CtaSinMedir>`? Para la analítica que no pasa por
 * aquí (los eventos de PostHog de la ventana del Plano pt) y que también tiene
 * que callar en una vista previa. */
export function useCtaSinMedir() {
  return useContext(SinMedirContext);
}

export default function CtaLink({ as: As = 'a', cta, onClick, children, ...rest }) {
  const sinMedir = useContext(SinMedirContext);
  const impressionRef = useCtaImpression(sinMedir ? null : cta);

  const handleClick = useCallback((event) => {
    // Primero el evento y después el handler del llamante: si ese handler
    // navega de forma síncrona (`navigate(...)`), para cuando el navegador
    // empiece a descargar la página el beacon ya está entregado.
    //
    // Nunca `preventDefault`: un CTA que deja de llevar a su destino porque el
    // tracking se puso por delante es mucho peor que un click sin medir.
    if (!sinMedir) trackCtaClick(cta);
    onClick?.(event);
  }, [cta, onClick, sinMedir]);

  return (
    <As ref={impressionRef} {...rest} onClick={handleClick}>
      {children}
    </As>
  );
}

/**
 * El mismo registro de click, suelto, para cuando envolver el elemento no sale
 * a cuenta. Es estable entre renders, así que se puede meter en una lista de
 * dependencias sin memoizar nada.
 */
export function useTrackCta() {
  return useContext(SinMedirContext) ? noTrack : trackCtaClick;
}

function noTrack() {}
